import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  fetchPropertyById,
  createProperty,
  updateProperty,
  uploadPropertyFile,
} from "../../lib/propertiesApi";
import AdminRoute from "../../components/AdminRoute";
import RichTextEditor from "../../components/RichTextEditor";
import PdfViewerModal from "../../components/PdfViewerModal";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import { sanitizeText, sanitizeTextInput, sanitizeInteger, sanitizeDecimal } from "../../lib/validation";
import { Plus, X, FileText, Image as ImageIcon, ChevronUp, ChevronDown, Eye } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = ["Available", "Featured", "Conditionally Sold", "Sold"];
const TYPE_OPTIONS = ["Duplex", "Fourplex", "Sixplex", "8-Plex", "8-Unit Building", "7-Plex"];

const defaultForm = {
  name: "",
  city: "",
  community: "",
  location: "",
  price: "",
  priceValue: "",
  type: "",
  units: "",
  status: "Available",
  dscr: "",
  capRate: "",
  cashOnCash: "",
  roi: "",
  roiAmount: "",
  cashFlow: "",
  downPayment: "",
  monthlyRent: "",
  equityGrowth: "",
  closingOn: "",
  lendingType: "",
  depositDetails: "",
  description: "",
  yearBuilt: "",
  squareFeet: "",
  lotSize: "",
  parking: "",
  heating: "",
  cooling: "",
  features: [],
  imageUrls: [],
  pdfs: {},
};

function AdminPropertyFormInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(defaultForm);
  const [featureInput, setFeatureInput] = useState("");
  // Ordered list: { type: 'existing', url } | { type: 'new', file, previewUrl }
  const [orderedImages, setOrderedImages] = useState([]);
  const [pdfEntries, setPdfEntries] = useState([]);
  const [pdfViewer, setPdfViewer] = useState({ open: false, url: null, title: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: existing, isLoading: loadingExisting } = useQuery({
    queryKey: ["admin-property", id],
    queryFn: () => fetchPropertyById(id).then((r) => r.data),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!existing) return;
    setForm({
      name: existing.name ?? "",
      city: existing.city ?? "",
      community: existing.community ?? "",
      location: existing.location ?? "",
      price: existing.price ?? "",
      priceValue: existing.priceValue ?? "",
      type: existing.type ?? "",
      units: existing.units ?? "",
      status: existing.status ?? "Available",
      dscr: existing.dscr ?? "",
      capRate: existing.capRate ?? "",
      cashOnCash: existing.cashOnCash ?? "",
      roi: existing.roi ?? "",
      roiAmount: existing.roiAmount ?? "",
      cashFlow: existing.cashFlow ?? "",
      downPayment: existing.downPayment ?? "",
      monthlyRent: existing.monthlyRent ?? "",
      equityGrowth: existing.equityGrowth ?? "",
      closingOn: existing.closingOn ?? "",
      lendingType: existing.lendingType ?? "",
      depositDetails: existing.depositDetails ?? "",
      description: existing.description ?? "",
      yearBuilt: existing.yearBuilt ?? "",
      squareFeet: existing.squareFeet ?? "",
      lotSize: existing.lotSize ?? "",
      parking: existing.parking ?? "",
      heating: existing.heating ?? "",
      cooling: existing.cooling ?? "",
      features: Array.isArray(existing.features) ? [...existing.features] : [],
      imageUrls: Array.isArray(existing.images) ? [...existing.images] : [],
      pdfs: existing.pdfs && typeof existing.pdfs === "object" ? { ...existing.pdfs } : {},
    });
    setOrderedImages(
      Array.isArray(existing.images)
        ? existing.images.map((url) => ({ type: "existing", url }))
        : []
    );
    if (existing.pdfs && typeof existing.pdfs === "object") {
      setPdfEntries(
        Object.entries(existing.pdfs).map(([label, url]) => ({ label, url, file: null }))
      );
    }
  }, [existing]);

  const imagePreviewUrlsRef = useRef([]);
  imagePreviewUrlsRef.current = orderedImages
    .filter((i) => i.type === "new" && i.previewUrl)
    .map((i) => i.previewUrl);
  useEffect(() => {
    return () => {
      imagePreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const onImageFilesChange = useCallback((files) => {
    const newItems = Array.from(files || []).map((file) => ({
      type: "new",
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setOrderedImages((prev) => [...prev, ...newItems]);
  }, []);

  const moveImage = useCallback((index, delta) => {
    setOrderedImages((prev) => {
      const next = [...prev];
      const ni = index + delta;
      if (ni < 0 || ni >= next.length) return prev;
      [next[index], next[ni]] = [next[ni], next[index]];
      return next;
    });
  }, []);

  const removeImage = useCallback((index) => {
    setOrderedImages((prev) => {
      const item = prev[index];
      if (item.type === "new" && item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const addFeature = () => {
    const t = featureInput.trim();
    if (!t) return;
    setForm((f) => ({ ...f, features: [...f.features, t] }));
    setFeatureInput("");
  };

  const removeFeature = (i) => {
    setForm((f) => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));
  };

  const addPdfRow = () => {
    setPdfEntries((e) => [...e, { label: "", file: null, url: null }]);
  };

  const setPdfRow = (index, field, value) => {
    setPdfEntries((e) => {
      const next = [...e];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const removePdfRow = (index) => {
    setPdfEntries((e) => e.filter((_, i) => i !== index));
  };

  const createMutation = useMutation({
    mutationFn: createProperty,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["admin-properties"] });
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast.success("Property created");
      navigate("/admin/dashboard");
    },
    onError: (err) => toast.error(err.message || "Create failed"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateProperty(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-properties"] });
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      queryClient.invalidateQueries({ queryKey: ["admin-property", id] });
      toast.success("Property updated");
      navigate("/admin/dashboard");
    },
    onError: (err) => toast.error(err.message || "Update failed"),
  });

  const submit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const trimFormStrings = (obj) => {
      const out = { ...obj };
      for (const k of Object.keys(out)) {
        if (typeof out[k] === "string") out[k] = out[k].trim();
        else if (Array.isArray(out[k])) out[k] = out[k].map((v) => (typeof v === "string" ? v.trim() : v));
      }
      return out;
    };
    const payload = trimFormStrings(form);

    const uploadImagesFromOrdered = async (pid) => {
      const urls = [];
      for (const item of orderedImages) {
        if (item.type === "existing") urls.push(item.url);
        else if (item.type === "new" && item.file) {
          const path = `${pid}/images/${Date.now()}-${item.file.name}`;
          urls.push(await uploadPropertyFile(item.file, path));
        }
      }
      return urls;
    };

    const buildPdfs = async (pid) => {
      const out = {};
      for (let i = 0; i < pdfEntries.length; i++) {
        const { label, file, url } = pdfEntries[i];
        if (!label) continue;
        if (url) out[label] = url;
        else if (file && pid) {
          const path = `${pid}/pdfs/${file.name}`;
          out[label] = await uploadPropertyFile(file, path);
        }
      }
      return out;
    };

    try {
      if (isEdit) {
        const imageUrls = await uploadImagesFromOrdered(id);
        const pdfs = await buildPdfs(id);
        updateMutation.mutate({
          id,
          payload: { ...payload, imageUrls, pdfs },
        });
      } else {
        const { data: created, error } = await createProperty({
          ...payload,
          imageUrls: [],
          pdfs: {},
        });
        if (error) throw error;
        const pid = created.id;
        const imageUrls = await uploadImagesFromOrdered(pid);
        const pdfs = await buildPdfs(pid);
        await updateProperty(pid, { ...payload, imageUrls, pdfs });
        queryClient.invalidateQueries({ queryKey: ["admin-properties"] });
        queryClient.invalidateQueries({ queryKey: ["properties"] });
        toast.success("Property created");
        navigate("/admin/dashboard");
      }
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const loading = loadingExisting && isEdit;
  const saving = isSubmitting || createMutation.isPending || updateMutation.isPending;

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground">Loading property…</p>
        </CardContent>
      </Card>
    );
  }

  const sectionClass = "rounded-xl border border-border bg-card p-6 shadow-sm";
  const sectionTitleClass = "text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4 pb-2 border-b border-border";

  return (
    <form onSubmit={submit}>
      <div className="space-y-8 max-w-4xl">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-primary">
            {isEdit ? "Edit property" : "Add property"}
          </h1>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => navigate("/admin/dashboard")}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-accent text-primary hover:bg-accent/90">
              {saving ? "Saving…" : isEdit ? "Update property" : "Create property"}
            </Button>
          </div>
        </div>

        {/* Basic info */}
        <section className={sectionClass}>
          <h2 className={sectionTitleClass}>Basic information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-foreground">Name</Label>
              <Input value={form.name} onChange={(e) => set("name", sanitizeTextInput(e.target.value, 200))} required className="h-11" placeholder="Property name" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Type</Label>
              <Select value={form.type || undefined} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label className="text-foreground">Address / Location</Label>
              <AddressAutocomplete
                value={form.location}
                onChange={(v) => set("location", (v || "").replace(/<[^>]*>/g, "").slice(0, 300))}
                onPlaceSelect={({ address, city, province, community }) => {
                  set("location", sanitizeText(address, 300));
                  if (city) set("city", sanitizeText(city, 100));
                  if (community) set("community", sanitizeText(community, 100));
                }}
                placeholder="Address"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">City</Label>
              <Input value={form.city} onChange={(e) => set("city", sanitizeTextInput(e.target.value, 100))} required className="h-11" placeholder="City" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Community</Label>
              <Input value={form.community} onChange={(e) => set("community", sanitizeTextInput(e.target.value, 100))} className="h-11" placeholder="Community" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Price (display)</Label>
              <Input value={form.price} onChange={(e) => set("price", sanitizeTextInput(e.target.value, 50))} placeholder="$2,865,789" required className="h-11" />
            </div>
              <div className="space-y-2">
                <Label className="text-foreground">Price value (for filters)</Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={form.priceValue}
                  onChange={(e) => set("priceValue", sanitizeInteger(e.target.value) || e.target.value)}
                  placeholder="2865789"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Units</Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={form.units}
                  onChange={(e) => set("units", sanitizeInteger(e.target.value) || e.target.value)}
                  className="h-11"
                />
              </div>
            <div className="space-y-2">
              <Label className="text-foreground">Status</Label>
              <Select value={form.status || undefined} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* Financials */}
        <section className={sectionClass}>
          <h2 className={sectionTitleClass}>Financials & lending</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {[
              { key: "dscr", label: "DSCR (%)", decimals: true },
              { key: "capRate", label: "Cap rate (%)", decimals: true },
              { key: "cashOnCash", label: "Cash on cash (%)", decimals: true },
              { key: "roi", label: "ROI (%)", decimals: true },
              { key: "roiAmount", label: "ROI amount", text: true },
              { key: "cashFlow", label: "Cash flow", text: true },
              { key: "downPayment", label: "Down payment", text: true },
              { key: "monthlyRent", label: "Monthly rent", text: true },
              { key: "equityGrowth", label: "Equity growth", text: true },
              { key: "closingOn", label: "Closing on", text: true },
              { key: "lendingType", label: "Lending type", text: true },
              { key: "depositDetails", label: "Deposit details", text: true },
            ].map(({ key, label, decimals, text }) => (
              <div key={key} className="space-y-2">
                <Label className="text-foreground text-sm">{label}</Label>
                <Input
                  value={form[key]}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (text) set(key, sanitizeTextInput(v, 500));
                    else if (decimals) set(key, v === "" ? "" : sanitizeDecimal(v, 2));
                    else set(key, v);
                  }}
                  className="h-10"
                  type={decimals ? "number" : "text"}
                  inputMode={decimals ? "decimal" : "text"}
                  step={decimals ? "0.01" : undefined}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Description */}
        <section className={sectionClass}>
          <h2 className={sectionTitleClass}>Description</h2>
          <div className="space-y-2">
            <Label className="text-foreground">Property overview</Label>
            <RichTextEditor
              value={form.description}
              onChange={(v) => set("description", v)}
              placeholder="Describe the property…"
              minHeight={200}
            />
          </div>
        </section>

        {/* Property details */}
        <section className={sectionClass}>
          <h2 className={sectionTitleClass}>Property details</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
            {[
              { key: "yearBuilt", label: "Year built", number: true },
              { key: "squareFeet", label: "Square feet", number: true },
              { key: "lotSize", label: "Lot size" },
              { key: "parking", label: "Parking" },
              { key: "heating", label: "Heating" },
              { key: "cooling", label: "Cooling" },
            ].map(({ key, label, number }) => (
              <div key={key} className="space-y-2">
                <Label className="text-foreground text-sm">{label}</Label>
                <Input
                  value={form[key]}
                  onChange={(e) => set(key, number ? sanitizeInteger(e.target.value) || e.target.value : sanitizeTextInput(e.target.value, 200))}
                  className="h-10"
                  type={number ? "number" : "text"}
                  inputMode={number ? "numeric" : "text"}
                  min={number ? 0 : undefined}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Key features */}
        <section className={sectionClass}>
          <h2 className={sectionTitleClass}>Key features</h2>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
                placeholder="e.g. (4) Three BR Main"
                className="h-11 flex-1"
              />
              <Button type="button" variant="outline" onClick={addFeature} className="shrink-0">
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
            {form.features.length > 0 && (
              <ul className="flex flex-wrap gap-2">
                {form.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-1.5 bg-muted/80 rounded-lg px-3 py-1.5 text-sm">
                    {f}
                    <button type="button" onClick={() => removeFeature(i)} className="text-destructive hover:bg-destructive/10 rounded p-0.5" aria-label="Remove">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Media */}
        <section className={sectionClass}>
          <h2 className={sectionTitleClass}>Images & documents</h2>
          <div className="space-y-6">
            <div>
              <Label className="flex items-center gap-2 text-foreground mb-2">
                <ImageIcon className="h-4 w-4 text-accent" />
                Images (first = main photo). Reorder with arrows.
              </Label>
              {orderedImages.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-3">
                  {orderedImages.map((item, i) => (
                    <div key={i} className="group relative flex flex-col items-center gap-1">
                      <div className="w-28 h-28 rounded-xl border-2 border-border overflow-hidden bg-muted shadow-sm">
                        <img
                          src={item.type === "existing" ? item.url : item.previewUrl}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex items-center gap-0.5">
                        <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => moveImage(i, -1)} disabled={i === 0} title="Move left">
                          <ChevronUp className="h-4 w-4 rotate-[-90deg]" />
                        </Button>
                        <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => moveImage(i, 1)} disabled={i === orderedImages.length - 1} title="Move right">
                          <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
                        </Button>
                        <Button type="button" variant="outline" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => removeImage(i)} title="Remove">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      {i === 0 && <span className="text-xs text-accent font-medium">Main</span>}
                    </div>
                  ))}
                </div>
              )}
              <Input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => { onImageFilesChange(e.target.files); e.target.value = ""; }}
                className="max-w-xs h-11 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-accent file:text-primary"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2 text-foreground mb-3">
                <FileText className="h-4 w-4 text-accent" />
                PDFs (label = button text on property page)
              </Label>
              <div className="space-y-3">
                {pdfEntries.map((entry, index) => (
                  <div key={index} className="flex gap-3 items-center p-3 rounded-lg bg-muted/40 border border-border">
                    <Input placeholder="e.g. Design & Floor Plans" value={entry.label} onChange={(e) => setPdfRow(index, "label", e.target.value)} className="flex-1 max-w-xs h-10" />
                    {entry.url ? (
                      <span className="flex items-center gap-2">
                        <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setPdfViewer({ open: true, url: entry.url, title: entry.label || "PDF" })}>
                          <Eye className="h-4 w-4" /> View
                        </Button>
                        <a href={entry.url} target="_blank" rel="noopener noreferrer" className="text-sm text-accent hover:underline truncate max-w-[120px]">Current file</a>
                      </span>
                    ) : (
                      <Input type="file" accept=".pdf" onChange={(e) => setPdfRow(index, "file", e.target.files?.[0] || null)} className="max-w-[200px] h-10 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs" />
                    )}
                    <Button type="button" variant="ghost" size="icon" onClick={() => removePdfRow(index)} className="shrink-0 text-muted-foreground hover:text-destructive">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addPdfRow} className="gap-2">
                  <Plus className="h-4 w-4" /> Add PDF
                </Button>
              </div>
            </div>
          </div>
        </section>

        <PdfViewerModal
          open={pdfViewer.open}
          onOpenChange={(open) => setPdfViewer((p) => ({ ...p, open }))}
          url={pdfViewer.url}
          title={pdfViewer.title}
        />

        <div className="flex justify-end gap-2 pb-8">
          <Button type="button" variant="outline" onClick={() => navigate("/admin/dashboard")}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving} className="bg-accent text-primary hover:bg-accent/90 min-w-[160px]">
            {saving ? "Saving…" : isEdit ? "Update property" : "Create property"}
          </Button>
        </div>
      </div>
    </form>
  );
}

export default function AdminPropertyForm() {
  return (
    <AdminRoute>
      <AdminPropertyFormInner />
    </AdminRoute>
  );
}
